import { useState, useEffect, lazy, Suspense } from 'react';
import {
  LanguageProvider,
  ThemeProvider,
  ToastProvider,
  ConfirmProvider,
  AuthProvider,
  OrganizationProvider,
  useAuth
} from '@contexts';
import { Loader, ErrorBoundary } from '@components/common';
import { AuthScreen } from '@pages/auth/AuthScreen';
import { EmailVerificationGate } from '@pages/auth/EmailVerificationGate';
import { MaintenanceScreen } from '@pages/public/MaintenanceScreen';
import { useMaintenanceMode } from '@hooks/useMaintenanceMode';

// Lazy-loaded: chunks separados que se descargan solo cuando se navega
// a la ruta correspondiente. Reduce el bundle inicial drásticamente
// (AdminPanel solo es accesible tras login; las webs públicas no necesitan
// cargar el admin; la mesa de partido no necesita ninguna otra cosa, etc).
const AdminPanel         = lazy(() => import('@pages/admin/AdminPanel').then(m => ({ default: m.AdminPanel })));
const PublicPage         = lazy(() => import('@pages/public/PublicPage').then(m => ({ default: m.PublicPage })));
const PublicTournamentBySlug = lazy(() => import('@pages/public/PublicTournamentBySlug').then(m => ({ default: m.PublicTournamentBySlug })));
const PublicRegistration = lazy(() => import('@pages/public/PublicRegistration').then(m => ({ default: m.PublicRegistration })));
const PublicPricing      = lazy(() => import('@pages/public/PublicPricing').then(m => ({ default: m.PublicPricing })));
const PublicGallery      = lazy(() => import('@pages/public/PublicGallery').then(m => ({ default: m.PublicGallery })));
const WebHome            = lazy(() => import('@pages/web/WebHome').then(m => ({ default: m.WebHome })));
const WebFeatures        = lazy(() => import('@pages/web/WebFeatures').then(m => ({ default: m.WebFeatures })));
const WebHowItWorks      = lazy(() => import('@pages/web/WebHowItWorks').then(m => ({ default: m.WebHowItWorks })));
const WebForOrgs         = lazy(() => import('@pages/web/WebForOrgs').then(m => ({ default: m.WebForOrgs })));
const WebContact         = lazy(() => import('@pages/web/WebContact').then(m => ({ default: m.WebContact })));
const Web404             = lazy(() => import('@pages/web/Web404').then(m => ({ default: m.Web404 })));
const MesaApp            = lazy(() => import('@pages/public/MesaApp').then(m => ({ default: m.MesaApp })));

// Fallback que se muestra mientras descarga un chunk
const RouteFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-secondary, #0a1228)'
  }}>
    <div className="spinner" style={{
      width: 40, height: 40,
      border: '3px solid rgba(240,216,120,0.2)',
      borderTopColor: '#f0d878',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const AppContent = ({ route }) => {
  const { user, userData, loading, isApproved, logout, mirrorFailed, needsEmailVerification } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-secondary)'
      }}>
        <div className="text-center">
          <div className="spinner" style={{margin: '0 auto var(--spacing-lg)'}}></div>
          <p className="text-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  // Home → Web comercial (nueva con WebLayout)
  if (route === '#/' || route === '' || route === '#' || route === '#/info') {
    return <MaintenanceWrapper isAdmin={false}><WebHome /></MaintenanceWrapper>;
  }

  // Páginas web nuevas (todas con WebLayout)
  if (route === '#/caracteristicas') {
    return <MaintenanceWrapper isAdmin={false}><WebFeatures /></MaintenanceWrapper>;
  }
  if (route === '#/como-funciona') {
    return <MaintenanceWrapper isAdmin={false}><WebHowItWorks /></MaintenanceWrapper>;
  }
  if (route === '#/para-quien') {
    return <MaintenanceWrapper isAdmin={false}><WebForOrgs /></MaintenanceWrapper>;
  }
  if (route === '#/contacto') {
    return <MaintenanceWrapper isAdmin={false}><WebContact /></MaintenanceWrapper>;
  }

  // Pricing page (migrada a WebLayout)
  if (route === '#/precios') {
    return <MaintenanceWrapper isAdmin={false}><PublicPricing /></MaintenanceWrapper>;
  }

  // Gallery page (migrada a WebLayout)
  if (route === '#/galeria') {
    return <MaintenanceWrapper isAdmin={false}><PublicGallery /></MaintenanceWrapper>;
  }

  // Registration form route
  if (route.startsWith('#/inscripcion/')) {
    const slug = route.replace('#/inscripcion/', '');
    return <MaintenanceWrapper isAdmin={false}><PublicRegistration slug={slug} /></MaintenanceWrapper>;
  }

  // Torneo público vía slug — URL compartible para redes / Google.
  // El bot de redes la atiende vía Cloud Function `tournamentRouter`
  // (firebase.json rewrite); el usuario llega a este componente.
  if (route.startsWith('#/torneo/')) {
    const slug = route.replace('#/torneo/', '');
    return <MaintenanceWrapper isAdmin={false}><PublicTournamentBySlug slug={slug} /></MaintenanceWrapper>;
  }

  // Lista de torneos + páginas legales
  if (route === '#/torneos' || route === '#/privacy' || route === '#/terms' || route === '#/legal') {
    const legalView = route === '#/privacy' ? 'privacy' : route === '#/terms' ? 'terms' : route === '#/legal' ? 'legal' : null;
    return <MaintenanceWrapper isAdmin={false}><PublicPage initialView={legalView} /></MaintenanceWrapper>;
  }

  // Registro público (tier free) — comparte AuthScreen con login para
  // mantener la animación de transición fluida entre ambos.
  if (route === '#/registro' || route === '#/register') {
    return <MaintenanceWrapper isAdmin={false}><AuthScreen initialMode="register" /></MaintenanceWrapper>;
  }

  // Admin routes
  if (route.startsWith('#/admin')) {
    // No user - show login (vía AuthScreen para compartir layout con /registro)
    if (!user) {
      return <AuthScreen initialMode="login" />;
    }

    // Email verification gate: si la cuenta requiere verificación y aún no se ha hecho.
    // Aplica a: autoregistrados (flag requiresEmailVerification) y a usuarios
    // legacy cuyo doc espejo no se pudo crear por tener el email sin verificar.
    if ((userData?.requiresEmailVerification || needsEmailVerification) && !user.emailVerified) {
      return <EmailVerificationGate user={user} />;
    }

    // El doc espejo users/{uid} no se pudo crear → el panel daría errores de
    // permisos en cada acción. Mejor mostrar pantalla de error con recarga.
    if (mirrorFailed) {
      return (
        <div className="login-page">
          <div className="login-container">
            <div className="login-card">
              <div className="login-header" style={{background: 'var(--color-danger)'}}>
                <div className="login-logo">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                </div>
                <h1 className="login-title">No se pudo cargar tu sesión</h1>
              </div>
              <div className="login-body text-center">
                <p style={{fontSize: '1.05rem', marginBottom: 'var(--spacing-lg)'}}>
                  Hubo un problema al preparar tu cuenta. Recarga la página para reintentar.
                </p>
                <div className="flex gap-md justify-center">
                  <button className="btn btn-primary" onClick={() => window.location.reload()}>
                    <i className="fa-solid fa-rotate-right"></i> Recargar
                  </button>
                  <button className="btn btn-ghost" onClick={logout}>
                    <i className="fa-solid fa-sign-out-alt"></i> Cerrar sesión
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // User exists but not in database or not approved
    if (!userData || !isApproved) {
      return (
        <div className="login-page">
          <div className="login-container">
            <div className="login-card">
              <div className="login-header" style={{background: 'var(--color-danger)'}}>
                <div className="login-logo">
                  <i className="fa-solid fa-ban"></i>
                </div>
                <h1 className="login-title">Acceso Denegado</h1>
              </div>
              <div className="login-body text-center">
                <p style={{fontSize: '1.1rem', marginBottom: 'var(--spacing-lg)'}}>
                  No tienes permisos para acceder al panel de administración.
                </p>
                <p className="text-muted mb-xl">
                  Contacta con el administrador si crees que esto es un error.
                </p>
                <div className="flex gap-md justify-center">
                  <a href="/" className="btn btn-outline">
                    <i className="fa-solid fa-home"></i> Ir al inicio
                  </a>
                  <button className="btn btn-ghost" onClick={logout}>
                    <i className="fa-solid fa-sign-out-alt"></i> Cerrar sesión
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return <AdminPanel />;
  }

  // Ruta desconocida → 404 personalizado
  return <MaintenanceWrapper isAdmin={false}><Web404 /></MaintenanceWrapper>;
};

const App = () => {
  // Detectar ruta desde hash O pathname (sin redirigir, así Google indexa /precios y no /#/precios).
  // Distinguimos entre ruta-hash (#/precios) y ancla CSS (#faq) — solo las que empiezan
  // con "#/" son rutas; las que son #anchor se ignoran para que pathname siga mandando
  // (así /precios#faq lleva a la página de precios + scroll automático a #faq).
  const getRouteFromUrl = () => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/')) return hash;
    const path = window.location.pathname;
    return path && path !== '/' ? `#${path}` : '#/';
  };

  const [route, setRoute] = useState(getRouteFromUrl());

  // La app ha montado con éxito → los chunks cargaron bien. Limpiamos el
  // contador de recargas por "chunk obsoleto" para que un futuro deploy
  // vuelva a tener sus reintentos disponibles.
  useEffect(() => {
    try { sessionStorage.removeItem('chunkReloadCount'); } catch {}
  }, []);

  useEffect(() => {
    const handler = () => setRoute(getRouteFromUrl());
    window.addEventListener('hashchange', handler);
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('hashchange', handler);
      window.removeEventListener('popstate', handler);
    };
  }, []);

  // Interceptar clicks en enlaces internos (/precios, /caracteristicas, etc.)
  // para usar History API y mantener URLs limpias sin recargar la página
  useEffect(() => {
    const handleClick = (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target.closest('a');
      if (!link) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//') || href.startsWith('/#')) return;
      e.preventDefault();
      if (window.location.pathname + window.location.hash !== href) {
        // View Transitions API: cross-fade automático del navegador entre rutas
        // (Chrome 111+, Edge 111+, Safari 18+). Fallback: navegación directa.
        const navigate = () => {
          window.history.pushState({}, '', href);
          setRoute(getRouteFromUrl());
          window.scrollTo({top: 0, behavior: 'instant'});
        };
        if (document.startViewTransition) {
          document.startViewTransition(navigate);
        } else {
          navigate();
        }
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Mesa de partido fuera del AuthProvider para evitar conflictos
  if (route === '#/mesa') {
    return (
      <LanguageProvider>
        <ThemeProvider>
          <MaintenanceWrapper isAdmin={false}>
            <Suspense fallback={<RouteFallback />}>
              <MesaApp />
            </Suspense>
          </MaintenanceWrapper>
        </ThemeProvider>
      </LanguageProvider>
    );
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <AuthProvider>
                <OrganizationProvider>
                  <Suspense fallback={<RouteFallback />}>
                    <AppContent route={route} />
                  </Suspense>
                </OrganizationProvider>
              </AuthProvider>
            </ConfirmProvider>
          </ToastProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
};

// Wrapper que muestra MaintenanceScreen si el modo mantenimiento está activo
const MaintenanceWrapper = ({ children, isAdmin }) => {
  const { maintenanceMode, checked } = useMaintenanceMode();
  if (!checked) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1628'}}>
        <div className="spinner" style={{width: 40, height: 40, border: '3px solid rgba(240,216,120,0.2)', borderTopColor: '#f0d878', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (maintenanceMode && !isAdmin) return <MaintenanceScreen />;
  return children;
};

export default App;
